import {
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Skeleton from '@/components/ui/custom/Skeleton';
import {
  Badge,
  Button,
  ComplexDateInput,
  Select,
  TextInput,
} from '@/components/ui/daisy';
import useDebounce from '@/data/hook/useDebounce';
import useToggle from '@/data/hook/useToggle';
import { accordionMarginTop } from '@/utils/animations/commom';

import {
  FilterBarAdvancedProps,
  FilterBarChipsProps,
  FilterBarContextValue,
  FilterBarDateInputProps,
  FilterBarFieldsGroupProps,
  FilterBarFieldsProps,
  FilterBarMoreFiltersProps,
  FilterBarProps,
  FilterBarSearchProps,
  FilterBarSelectProps,
} from './types';

const SEARCH_DEBOUNCE_TIME_IN_MS = 500;

const FilterBarContext = createContext<FilterBarContextValue>({
  isAdvancedOpen: false,
  toggleAdvanced: () => {},
});

const useFilterBarContext = () => useContext(FilterBarContext);

const FilterBarBase = ({ className, children, ...props }: FilterBarProps) => {
  const { isOpen, toggle } = useToggle();

  const contextValue = useMemo<FilterBarContextValue>(
    () => ({ isAdvancedOpen: isOpen, toggleAdvanced: toggle }),
    [isOpen, toggle],
  );

  return (
    <FilterBarContext.Provider value={contextValue}>
      <div className={twMerge('flex w-full flex-col', className)} {...props}>
        {children}
      </div>
    </FilterBarContext.Provider>
  );
};

const Fields = ({ className, children, ...props }: FilterBarFieldsProps) => (
  <div
    className={twMerge(
      'flex w-full items-end gap-4 justify-between',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

const FieldsGroup = ({
  className,
  children,
  ...props
}: FilterBarFieldsGroupProps) => (
  <div className={twMerge('flex flex-1 items-end gap-4', className)} {...props}>
    {children}
  </div>
);

const Search = ({
  label,
  placeholder,
  className,
  onSearch,
  debounceTime = SEARCH_DEBOUNCE_TIME_IN_MS,
  defaultValue,
  ...props
}: FilterBarSearchProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'filterBar' });
  const { debounce, cancel } = useDebounce(debounceTime);
  const [value, setValue] = useState(String(defaultValue ?? ''));

  useEffect(() => cancel, [cancel]);

  const handleClear = () => {
    cancel();
    setValue('');
    onSearch('');
  };

  return (
    <TextInput.Fieldset label={label ?? t('search')} className="flex-1">
      <TextInput.Wrapper className="w-full">
        <TextInput.Icon position="start">
          <MagnifyingGlassIcon className="size-3.5 opacity-50" />
        </TextInput.Icon>
        <TextInput
          type="search"
          placeholder={placeholder ?? t('searchPlaceholder')}
          className={className}
          value={value}
          onChange={event => {
            const { value: newValue } = event.target;
            setValue(newValue);
            debounce(() => onSearch(newValue));
          }}
          onClear={handleClear}
          {...props}
        />
      </TextInput.Wrapper>
    </TextInput.Fieldset>
  );
};

const FilterBarSelect = ({
  label,
  className,
  children,
  ...props
}: FilterBarSelectProps) => (
  <Select.Fieldset label={label} className="flex-1">
    <Select className={twMerge('w-full', className)} {...props}>
      {children}
    </Select>
  </Select.Fieldset>
);

const DateInput = ({ label, ...props }: FilterBarDateInputProps) => (
  <ComplexDateInput.Fieldset label={label} className="flex-1">
    <ComplexDateInput {...props} />
  </ComplexDateInput.Fieldset>
);

const MoreFilters = ({ count = 0, className }: FilterBarMoreFiltersProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'filterBar' });
  const { isAdvancedOpen, toggleAdvanced } = useFilterBarContext();

  return (
    <Button
      type="button"
      variant="outline"
      className={twMerge('mb-1', className)}
      aria-expanded={isAdvancedOpen}
      onClick={toggleAdvanced}
    >
      <FunnelIcon className="size-3.5" />
      {t(isAdvancedOpen ? 'lessFilters' : 'moreFilters')}
      <ConditionalRenderer condition={count > 0}>
        <Badge color="primary" size="sm" className="rounded-full">
          {count}
        </Badge>
      </ConditionalRenderer>
    </Button>
  );
};

const Advanced = ({
  className,
  children,
  onClearAll,
}: FilterBarAdvancedProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'filterBar' });
  const { isAdvancedOpen } = useFilterBarContext();
  const [isAnimating, setIsAnimating] = useState(true);

  return (
    <AnimatePresence>
      {isAdvancedOpen && (
        <motion.section
          {...accordionMarginTop}
          onAnimationStart={() => setIsAnimating(true)}
          onAnimationComplete={() => setIsAnimating(false)}
          className={isAnimating ? 'overflow-hidden' : 'overflow-visible'}
        >
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelIcon className="size-5 text-primary" />
              <h3 className="font-semibold text-base-content">
                {t('moreFilters')}
              </h3>
            </div>
            <ConditionalRenderer condition={!!onClearAll}>
              <Button
                type="button"
                variant="link"
                color="error"
                size="xs"
                onClick={onClearAll}
              >
                {t('clearAll')}
              </Button>
            </ConditionalRenderer>
          </header>
          <div
            className={twMerge('flex w-full items-end gap-4 pt-4', className)}
          >
            {children}
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};

const Chips = ({
  chips,
  onRemove,
  onClearAll,
  className,
}: FilterBarChipsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'filterBar' });
  const { isAdvancedOpen } = useFilterBarContext();

  if (isAdvancedOpen || !chips.length) return null;

  return (
    <div
      className={twMerge(
        'flex w-full flex-wrap items-center gap-2 pt-3',
        className,
      )}
    >
      {chips.map(chip => (
        <Badge key={chip.key} color="primary" variant="soft">
          <ConditionalRenderer
            condition={!!chip.isLoading}
            fallback={
              <Fragment>
                {chip.label}
                <button
                  type="button"
                  aria-label={t('removeFilter', { label: chip.label })}
                  onClick={() => onRemove(chip.key)}
                >
                  <XMarkIcon className="size-3" />
                </button>
              </Fragment>
            }
          >
            <Skeleton className="h-3 w-16 rounded-full" />
          </ConditionalRenderer>
        </Badge>
      ))}
      <Button
        type="button"
        variant="link"
        color="error"
        size="xs"
        onClick={onClearAll}
      >
        {t('clearAll')}
      </Button>
    </div>
  );
};

FilterBarBase.displayName = 'FilterBar';
Fields.displayName = 'FilterBar.Fields';
FieldsGroup.displayName = 'FilterBar.FieldsGroup';
Search.displayName = 'FilterBar.Search';
FilterBarSelect.displayName = 'FilterBar.Select';
DateInput.displayName = 'FilterBar.DateInput';
MoreFilters.displayName = 'FilterBar.MoreFilters';
Advanced.displayName = 'FilterBar.Advanced';
Chips.displayName = 'FilterBar.Chips';

export const FilterBar = Object.assign(FilterBarBase, {
  Fields,
  FieldsGroup,
  Search,
  Select: FilterBarSelect,
  DateInput,
  MoreFilters,
  Advanced,
  Chips,
});
